import { Icon, cn, type IconName } from "@sagot/ui";
import type { ReactNode } from "react";
import { NEUTRAL_STATUS_PHRASES, useRotatingStatus } from "../neutral-status";
import type { ChatStage } from "../types";
import { useChatTheme } from "./chat-theme";

const RISE = "animate-rise motion-reduce:animate-none";

export function UserBubble({ text, footer, className }: { text: string; footer?: ReactNode; className?: string }) {
  const { themed } = useChatTheme();
  return (
    <div className={cn("flex flex-col items-end", RISE, className)}>
      <div
        className={
          themed
            ? "ml-auto max-w-[80%] whitespace-pre-wrap rounded-[var(--wg-radius,14px)] rounded-br-xs bg-[var(--wg-primary)] px-3.5 py-2.5 text-sm leading-relaxed text-[var(--wg-on-primary)]"
            : "bubble-user whitespace-pre-wrap"
        }
      >
        {text}
      </div>
      {footer}
    </div>
  );
}

/** Default assistant avatar (sparkles on navy / brand in dark). */
export function BotAvatar() {
  return (
    <span aria-hidden="true" className="inline-flex size-7 shrink-0 items-center justify-center rounded-pill bg-ink-950 text-brand-500 dark:bg-brand-500 dark:text-ink-950">
      <Icon name="sparkles" className="size-3.5" />
    </span>
  );
}

/** Three bouncing dots; static under prefers-reduced-motion. */
export function TypingIndicator({ label }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden="true" className="typing-dot motion-reduce:animate-none" />
      <span aria-hidden="true" className="typing-dot [animation-delay:150ms] motion-reduce:animate-none" />
      <span aria-hidden="true" className="typing-dot [animation-delay:300ms] motion-reduce:animate-none" />
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}

export function StreamingCaret() {
  return <span aria-hidden="true" data-testid="caret" className="caret motion-reduce:animate-none" />;
}

/**
 * Who reads the chat. "visitor" (default: a client, student, or anyone on the widget / web app) never learns HOW the
 * answer is found, so the status is a neutral rotating phrase. "staff" (playground, workflow preview) keeps the
 * stage wording, which is useful to the operator tuning the agent.
 */
export type ChatAudience = "visitor" | "staff";

/** Staff-only stage wording. Visitors never see these: see `NeutralStatus`. */
export const STAGE_LABELS: Record<ChatStage, string> = {
  understanding: "Reading your question…",
  retrieving: "Searching documents…",
  reranking: "Checking the best sources…",
  generating: "Writing answer…",
  validating: "Double-checking the answer…",
};

/**
 * Typing dots + a phrase that rotates every 2.5 s. One fixed-height line, so the swap moves nothing around it. The
 * visible phrase is hidden from assistive tech; ONE polite live region announces the first phrase once.
 */
export function NeutralStatus({ phrases = NEUTRAL_STATUS_PHRASES }: { phrases?: readonly string[] }) {
  const { phrase, announcement } = useRotatingStatus(phrases);
  return (
    <span className="inline-flex h-5 max-w-full items-center gap-1.5 text-xs text-fg-subtle" data-thinking-status="">
      <TypingIndicator />
      <span key={phrase} aria-hidden="true" data-status-phrase="" className="ml-1 truncate whitespace-nowrap animate-fade-in motion-reduce:animate-none">
        {phrase}
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </span>
  );
}

export interface StatusLineProps {
  stage?: ChatStage;
  /** Staff only: per-stage wording. */
  labels?: Partial<Record<ChatStage, string>>;
  audience?: ChatAudience;
  /** Visitor only: the localized rotation (defaults to English). */
  phrases?: readonly string[];
}

export function StatusLine({ stage, labels, audience = "visitor", phrases }: StatusLineProps) {
  if (audience !== "staff") return <NeutralStatus phrases={phrases} />;
  const label = { ...STAGE_LABELS, ...labels }[stage ?? "retrieving"];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg-subtle">
      <TypingIndicator />
      <span className="ml-1">{label}</span>
    </span>
  );
}

export interface BotBubbleProps {
  name: string;
  avatar?: ReactNode;
  /** Rendered answer body (markdown output). Empty while waiting → status line. */
  children?: ReactNode;
  waiting?: boolean;
  streaming?: boolean;
  stage?: ChatStage;
  /** Staff audience only. */
  stageLabels?: Partial<Record<ChatStage, string>>;
  audience?: ChatAudience;
  /** Visitor audience: the localized neutral rotation. */
  statusPhrases?: readonly string[];
  /** Citations, action card, follow-ups, feedback — in that order. */
  after?: ReactNode;
  /** Id of the text bubble: a workflow card's buttons are described by it. */
  textId?: string;
  className?: string;
}

export function BotBubble({ name, avatar, children, waiting, streaming, stage, stageLabels, audience, statusPhrases, after, textId, className }: BotBubbleProps) {
  return (
    <div className={cn("flex items-start gap-2.5", RISE, className)}>
      {avatar ?? <BotAvatar />}
      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[11px] font-bold text-fg-muted">{name}</div>
        <div id={textId} className="bubble-bot" aria-busy={streaming === true || waiting === true ? true : undefined}>
          {waiting ? <StatusLine stage={stage} labels={stageLabels} audience={audience} phrases={statusPhrases} /> : children}
          {streaming && !waiting ? <StreamingCaret /> : null}
        </div>
        {after}
      </div>
    </div>
  );
}

export function StaffBubble({ author, staffRole, initials, text, badge }: { author: string; staffRole?: string; initials: string; text: string; badge?: string }) {
  return (
    <div className={cn("flex items-start gap-2.5", RISE)}>
      <span aria-hidden="true" className="avatar size-7 bg-signal-100 text-signal-700">
        {initials}
      </span>
      <div className="min-w-0">
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold text-fg-muted">
          {staffRole ? `${author} · ${staffRole}` : author}
          {badge ? <span className="badge badge-warning">{badge}</span> : null}
        </div>
        <div className="bubble-bot whitespace-pre-wrap">{text}</div>
      </div>
    </div>
  );
}

export type SystemNoteTone = "neutral" | "success";

/** Centered pill (handoff progress, language switch, history restored…). */
export function SystemNote({ icon, tone = "neutral", typing = false, children }: { icon?: IconName; tone?: SystemNoteTone; typing?: boolean; children: ReactNode }) {
  return (
    <div className={cn("flex justify-center", RISE)}>
      <div
        className={cn(
          "inline-flex items-center gap-2 rounded-pill px-3 py-1.5 text-[11px] font-semibold",
          tone === "success" ? "bg-success-soft text-success" : "bg-sunken text-fg-muted",
        )}
      >
        {typing ? <TypingIndicator /> : null}
        {icon ? <Icon name={icon} className="size-3.5" /> : null}
        {children}
      </div>
    </div>
  );
}
